import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import DeleteSweepRoundedIcon from '@mui/icons-material/DeleteSweepRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
  cleanupPatternPerformanceDuplicates,
  fetchPatternPerformanceCalibration,
  fetchPatternPerformanceComparison,
  fetchPatternPerformanceDetail,
  fetchPatternPerformanceSummary,
  purgeLegacyPatternPerformanceV1,
  purgePatternPerformanceHistory,
} from '../../api/adminPatternPerformance';

const DEFAULT_FILTERS = {
  version: '',
  engineBuild: '',
  pattern: '',
  patternFamily: '',
  crypto: '',
  exchange: '',
  timeframe: '',
  direction: '',
  status: '',
  scoreMin: '',
  scoreMax: '',
  scoreBucket: '',
  mtfScoreMin: '',
  mtfScoreMax: '',
  mtfBucket: '',
  mtfAlignment: '',
  confidenceBucket: '',
  assetType: '',
  volumeConfirmed: '',
  dateFrom: '',
  dateTo: '',
};

const STANDARD_BUCKET_OPTIONS = ['<50', '50-59', '60-69', '70-79', '80-89', '90-100'];
const MTF_BUCKET_OPTIONS = [...STANDARD_BUCKET_OPTIONS, 'UNKNOWN'];
const MTF_ALIGNMENT_OPTIONS = ['STRONG', 'MIXED', 'CONFLICT', 'UNKNOWN'];
const ASSET_TYPE_OPTIONS = [
  { value: '', label: 'Hors stablecoins (défaut)' },
  { value: 'CRYPTO', label: 'Crypto' },
  { value: 'STABLECOIN', label: 'Stablecoins seulement' },
  { value: 'UNKNOWN', label: 'Type inconnu / legacy' },
];
const TAB_OPTIONS = [
  { value: 'overview', label: 'Vue d ensemble' },
  { value: 'patterns', label: 'Par pattern' },
  { value: 'versions', label: 'Par version' },
  { value: 'score', label: 'Par score' },
  { value: 'mtf', label: 'MTF' },
  { value: 'calibration', label: 'Calibration' },
];
const PAGE_COLORS = {
  ink: '#17323c',
  inkSoft: '#355b63',
  sand: '#fffaf2',
  mist: '#edf3f6',
  border: '#e6d9c7',
  muted: '#4f646f',
  success: '#2f7d5d',
  warning: '#b57422',
  danger: '#9c4f4f',
  accent: '#c07c32',
};

function toNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
}

function normalizeRatio(value) {
  const numeric = toNumber(value);
  if (numeric === null) {
    return null;
  }
  return Math.abs(numeric) > 1.5 ? numeric / 100 : numeric;
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function formatPercent(value, fallback = 'n/a') {
  const normalized = normalizeRatio(value);
  if (normalized === null) {
    return fallback;
  }
  return `${(normalized * 100).toFixed(1)}%`;
}

function formatDecimal(value, digits = 1, fallback = 'n/a') {
  const numeric = toNumber(value);
  if (numeric === null) {
    return fallback;
  }
  return numeric.toFixed(digits);
}

function formatScore(value, digits = 0, fallback = 'n/a') {
  return formatDecimal(value, digits, fallback);
}

function formatDuration(seconds) {
  const numeric = toNumber(seconds);
  if (numeric === null || numeric <= 0) {
    return 'n/a';
  }
  const totalSeconds = Math.round(numeric);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  if (days > 0) {
    return `${days}j ${hours}h`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

function formatDateTime(value) {
  if (!value) {
    return 'n/a';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('fr-FR');
}

function parseVersionsInput(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
    .join(',');
}

function formatValue(value, fallback = 'n/a') {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }
  return value;
}

function normalizeFilters(filters = {}) {
  return Object.fromEntries(
    Object.entries({ ...DEFAULT_FILTERS, ...filters }).map(([key, value]) => {
      if (typeof value !== 'string') {
        return [key, value];
      }
      const trimmed = value.trim();
      return [key, key === 'version' ? parseVersionsInput(trimmed) : trimmed];
    }),
  );
}

function bucketOrder(label) {
  if (!label) {
    return 10_000;
  }
  if (label === 'UNKNOWN') {
    return 9_999;
  }
  if (label === '<50') {
    return 0;
  }
  const match = String(label).match(/(\d+)/);
  return match ? Number(match[1]) : 5_000;
}

function orderBucketLabels(labels = []) {
  return [...new Set((labels || []).filter(Boolean))].sort((left, right) => {
    const diff = bucketOrder(left) - bucketOrder(right);
    return diff !== 0 ? diff : String(left).localeCompare(String(right));
  });
}

function calibrationFallbackFromBuckets(rows = []) {
  return (rows || []).map((row) => ({
    bucket: row.bucket || row.label,
    minValue: row.minValue ?? row.minConfidence ?? null,
    maxValue: row.maxValue ?? row.maxConfidence ?? null,
    sampleSize: row.sampleSize ?? row.detections ?? 0,
    breakoutRate: row.breakoutRate ?? 0,
    targetRate: row.targetRate ?? row.targetFinalRate ?? 0,
    invalidationRate: row.invalidationRate ?? 0,
    medianTimeToTargetSeconds: row.medianTimeToTargetSeconds ?? row.timeToTargetFinal?.medianSeconds ?? null,
    lowSample: Boolean(row.lowSample),
  }));
}

function matrixCellBackground(targetRate, lowSample) {
  const normalized = normalizeRatio(targetRate);
  if (normalized === null) {
    return alpha(PAGE_COLORS.ink, 0.05);
  }
  return alpha(lowSample ? PAGE_COLORS.warning : PAGE_COLORS.inkSoft, 0.14 + clamp(normalized, 0, 1) * 0.56);
}

function LowSampleChip({ lowSample, threshold }) {
  if (!lowSample) {
    return null;
  }
  return (
    <Chip
      size="small"
      label={`Echantillon < ${threshold}`}
      sx={{
        height: 24,
        bgcolor: alpha(PAGE_COLORS.warning, 0.12),
        color: PAGE_COLORS.warning,
        fontWeight: 700,
      }}
    />
  );
}

function MetricCard({ label, value, helper, tone = 'default' }) {
  const toneMap = {
    default: { bg: '#fff', fg: PAGE_COLORS.ink },
    accent: { bg: alpha(PAGE_COLORS.accent, 0.08), fg: PAGE_COLORS.accent },
    success: { bg: alpha(PAGE_COLORS.success, 0.1), fg: PAGE_COLORS.success },
    warning: { bg: alpha(PAGE_COLORS.warning, 0.1), fg: PAGE_COLORS.warning },
    danger: { bg: alpha(PAGE_COLORS.danger, 0.1), fg: PAGE_COLORS.danger },
  };
  const palette = toneMap[tone] || toneMap.default;

  return (
    <Paper
      sx={{
        p: 2.2,
        borderRadius: 4,
        background: palette.bg,
        border: `1px solid ${alpha(palette.fg, 0.12)}`,
      }}
    >
      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ fontWeight: 900, color: palette.fg, mt: 0.6 }}>
        {value}
      </Typography>
      {helper ? (
        <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, mt: 0.8 }}>
          {helper}
        </Typography>
      ) : null}
    </Paper>
  );
}

function SectionCard({ icon, title, description, children, action }) {
  return (
    <Paper sx={{ p: 3, borderRadius: 5, background: '#fff', border: `1px solid ${alpha(PAGE_COLORS.ink, 0.08)}` }}>
      <Stack spacing={2.1}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Stack direction="row" spacing={1.1} alignItems="center">
            {icon}
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                {title}
              </Typography>
              {description ? (
                <Typography variant="body2" sx={{ color: PAGE_COLORS.muted, mt: 0.35 }}>
                  {description}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          {action || null}
        </Stack>
        {children}
      </Stack>
    </Paper>
  );
}

function RateBar({ label, value, color }) {
  const normalized = clamp(normalizeRatio(value) || 0, 0, 1);

  return (
    <Stack spacing={0.6}>
      <Stack direction="row" justifyContent="space-between" spacing={1}>
        <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>{label}</Typography>
        <Typography variant="body2" sx={{ fontWeight: 800, color }}>{formatPercent(value)}</Typography>
      </Stack>
      <Box sx={{ height: 10, borderRadius: 999, overflow: 'hidden', bgcolor: alpha(color, 0.12) }}>
        <LinearProgress
          variant="determinate"
          value={normalized * 100}
          sx={{
            height: '100%',
            bgcolor: alpha(color, 0.08),
            '& .MuiLinearProgress-bar': {
              bgcolor: color,
            },
          }}
        />
      </Box>
    </Stack>
  );
}

function BucketMetricsTable({ rows, lowSampleThreshold, firstLabel = 'Bucket' }) {
  if (!rows?.length) {
    return <Alert severity="info">Aucune ligne ne correspond aux filtres actifs.</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>{firstLabel}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Median target</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.bucket || row.label}-${row.minValue ?? row.minConfidence ?? 'na'}`} hover>
              <TableCell>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                    {row.bucket || row.label}
                  </Typography>
                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                </Stack>
              </TableCell>
              <TableCell>{row.detections}</TableCell>
              <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
              <TableCell>{formatPercent(row.targetFinalRate)}</TableCell>
              <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
              <TableCell>{formatDuration(row.timeToTargetFinal?.medianSeconds || row.medianTimeToTargetSeconds)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function GroupedMetricsTable({ rows, groupLabel, lowSampleThreshold }) {
  if (!rows?.length) {
    return <Alert severity="info">Aucune agrégation n est disponible pour cette dimension.</Alert>;
  }

  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 800 }}>{groupLabel}</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Bucket</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
            <TableCell sx={{ fontWeight: 800 }}>Median target</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={`${row.group}-${row.bucket}`} hover>
              <TableCell>
                <Typography variant="body2" sx={{ fontWeight: 700, color: PAGE_COLORS.ink }}>
                  {row.group}
                </Typography>
              </TableCell>
              <TableCell>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                  <Typography variant="body2">{row.bucket}</Typography>
                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                </Stack>
              </TableCell>
              <TableCell>{row.detections}</TableCell>
              <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
              <TableCell>{formatPercent(row.targetFinalRate)}</TableCell>
              <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
              <TableCell>{formatDuration(row.timeToTargetFinal?.medianSeconds)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CalibrationPanel({ title, description, rows, lowSampleThreshold }) {
  return (
    <SectionCard
      icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
      title={title}
      description={description}
    >
      {!rows?.length ? (
        <Alert severity="info">Aucune calibration n est encore disponible.</Alert>
      ) : (
        <Stack spacing={1.2}>
          {rows.map((row) => (
            <Paper
              key={row.bucket}
              variant="outlined"
              sx={{
                p: 1.6,
                borderRadius: 3,
                borderColor: alpha(row.lowSample ? PAGE_COLORS.warning : PAGE_COLORS.inkSoft, 0.16),
                bgcolor: row.lowSample ? alpha(PAGE_COLORS.warning, 0.05) : PAGE_COLORS.sand,
              }}
            >
              <Stack spacing={1.1}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                      {row.bucket}
                    </Typography>
                    <Chip size="small" label={`${row.sampleSize} détection(s)`} sx={{ fontWeight: 700 }} />
                    <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                  </Stack>
                  <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>
                    Median target {formatDuration(row.medianTimeToTargetSeconds)}
                  </Typography>
                </Stack>
                <RateBar label="Breakout" value={row.breakoutRate} color={PAGE_COLORS.inkSoft} />
                <RateBar label="Target final" value={row.targetRate} color={PAGE_COLORS.success} />
                <RateBar label="Invalidation" value={row.invalidationRate} color={PAGE_COLORS.danger} />
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}

function ScoreMtfMatrix({ cells, lowSampleThreshold }) {
  const scoreBuckets = useMemo(() => orderBucketLabels((cells || []).map((cell) => cell.scoreBucket)), [cells]);
  const mtfBuckets = useMemo(() => orderBucketLabels((cells || []).map((cell) => cell.mtfBucket)), [cells]);
  const lookup = useMemo(
    () => new Map((cells || []).map((cell) => [`${cell.scoreBucket}__${cell.mtfBucket}`, cell])),
    [cells],
  );

  if (!cells?.length) {
    return <Alert severity="info">Aucune matrice score × MTF n est disponible pour la cohorte courante.</Alert>;
  }

  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Box sx={{ minWidth: 720, display: 'grid', gridTemplateColumns: `180px repeat(${mtfBuckets.length}, minmax(132px, 1fr))`, gap: 1 }}>
        <Box sx={{ p: 1.2 }} />
        {mtfBuckets.map((bucket) => (
          <Paper key={bucket} sx={{ p: 1.2, borderRadius: 3, bgcolor: PAGE_COLORS.sand, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
              MTF
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{bucket}</Typography>
          </Paper>
        ))}
        {scoreBuckets.map((scoreBucket) => (
          <React.Fragment key={scoreBucket}>
            <Paper sx={{ p: 1.4, borderRadius: 3, bgcolor: PAGE_COLORS.sand }}>
              <Typography variant="caption" sx={{ color: PAGE_COLORS.muted, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                Score
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{scoreBucket}</Typography>
            </Paper>
            {mtfBuckets.map((mtfBucket) => {
              const cell = lookup.get(`${scoreBucket}__${mtfBucket}`);
              return (
                <Paper
                  key={`${scoreBucket}-${mtfBucket}`}
                  sx={{
                    p: 1.35,
                    borderRadius: 3,
                    minHeight: 110,
                    bgcolor: matrixCellBackground(cell?.targetFinalRate, cell?.lowSample),
                    border: `1px solid ${alpha(cell?.lowSample ? PAGE_COLORS.warning : PAGE_COLORS.ink, 0.12)}`,
                  }}
                >
                  {!cell ? (
                    <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>n/a</Typography>
                  ) : (
                    <Stack spacing={0.6}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.8} justifyContent="space-between">
                        <Typography variant="body2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>
                          {formatPercent(cell.targetFinalRate)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                          n={cell.detections}
                        </Typography>
                      </Stack>
                      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                        Breakout {formatPercent(cell.breakoutRate)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                        Invalid. {formatPercent(cell.invalidationRate)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>
                        Median {formatDuration(cell.timeToTargetFinal?.medianSeconds)}
                      </Typography>
                      <LowSampleChip lowSample={cell.lowSample} threshold={lowSampleThreshold} />
                    </Stack>
                  )}
                </Paper>
              );
            })}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
}

const PatternPerformanceAdminPage = () => {
  const [filters, setFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...DEFAULT_FILTERS }));
  const [summary, setSummary] = useState(null);
  const [comparison, setComparison] = useState({});
  const [calibration, setCalibration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [calibrationError, setCalibrationError] = useState('');
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [cleanupError, setCleanupError] = useState('');
  const [cleanupResult, setCleanupResult] = useState(null);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [purgeError, setPurgeError] = useState('');
  const [purgeResult, setPurgeResult] = useState(null);
  const [legacyPurgeLoading, setLegacyPurgeLoading] = useState(false);
  const [legacyPurgeError, setLegacyPurgeError] = useState('');
  const [legacyPurgeResult, setLegacyPurgeResult] = useState(null);
  const [resetNotice, setResetNotice] = useState('');

  const loadData = useCallback(async (nextFilters) => {
    setLoading(true);
    setError('');
    setCalibrationError('');
    try {
      const normalizedFilters = normalizeFilters(nextFilters);
      const [summaryPayload, comparisonPayload] = await Promise.all([
        fetchPatternPerformanceSummary(normalizedFilters),
        fetchPatternPerformanceComparison({
          ...normalizedFilters,
          versions: parseVersionsInput(normalizedFilters.version),
        }),
      ]);
      const calibrationPayload = await fetchPatternPerformanceCalibration(normalizedFilters).catch((loadError) => {
        setCalibrationError(loadError.message || 'Impossible de charger la calibration score / MTF.');
        return null;
      });
      setSummary(summaryPayload);
      setComparison(comparisonPayload || {});
      setCalibration(calibrationPayload);
    } catch (loadError) {
      setSummary(null);
      setComparison({});
      setCalibration(null);
      setError(loadError.message || 'Impossible de charger les statistiques de performance patterns.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(appliedFilters);
  }, [appliedFilters, loadData]);

  const handleFilterChange = (key) => (event) => {
    setResetNotice('');
    setFilters((current) => ({
      ...current,
      [key]: event.target.value,
    }));
  };

  const handleApplyFilters = () => {
    setResetNotice('');
    setCleanupError('');
    setCleanupResult(null);
    setPurgeError('');
    setPurgeResult(null);
    setLegacyPurgeError('');
    setLegacyPurgeResult(null);
    setAppliedFilters(normalizeFilters(filters));
  };

  const handleResetFilters = () => {
    const resetFilters = { ...DEFAULT_FILTERS };
    setFilters(resetFilters);
    setAppliedFilters(resetFilters);
    setResetNotice('Filtres réinitialisés. Retour à la cohorte par défaut (hors stablecoins).');
    setCleanupError('');
    setCleanupResult(null);
    setPurgeError('');
    setPurgeResult(null);
    setLegacyPurgeError('');
    setLegacyPurgeResult(null);
  };

  const handleCleanupDuplicates = useCallback(async () => {
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const confirmed = window.confirm('Nettoyer les doublons de la cohorte actuellement affichée ? Les détections dupliquées et leurs événements liés seront supprimés.');
      if (!confirmed) {
        return;
      }
    }

    setCleanupLoading(true);
    setResetNotice('');
    setCleanupError('');
    setCleanupResult(null);
    try {
      const normalizedFilters = normalizeFilters(appliedFilters);
      const payload = await cleanupPatternPerformanceDuplicates(normalizedFilters);
      setCleanupResult(payload);
      await loadData(normalizedFilters);
    } catch (loadError) {
      setCleanupError(loadError.message || 'Impossible de nettoyer les doublons de cette cohorte.');
    } finally {
      setCleanupLoading(false);
    }
  }, [appliedFilters, loadData]);

  const handlePurgeHistory = useCallback(async () => {
    const normalizedFilters = normalizeFilters(filters);
    if (!normalizedFilters.dateFrom || !normalizedFilters.dateTo) {
      setPurgeError('Renseigne Date début et Date fin avant de lancer la purge historique.');
      setPurgeResult(null);
      return;
    }

    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const confirmed = window.confirm('Purger définitivement l historique Pattern Performance avec les filtres et la période actuellement saisis ? Les détections et leurs événements liés seront supprimés.');
      if (!confirmed) {
        return;
      }
    }

    setPurgeLoading(true);
    setResetNotice('');
    setPurgeError('');
    setPurgeResult(null);
    try {
      const payload = await purgePatternPerformanceHistory(normalizedFilters);
      setFilters(normalizedFilters);
      setAppliedFilters(normalizedFilters);
      setPurgeResult(payload);
    } catch (loadError) {
      setPurgeError(loadError.message || 'Impossible de purger l historique de cette période.');
    } finally {
      setPurgeLoading(false);
    }
  }, [filters]);

  const handlePurgeLegacyV1 = useCallback(async () => {
    if (typeof window !== 'undefined' && typeof window.confirm === 'function') {
      const confirmed = window.confirm('Purger définitivement toutes les données legacy v1 du Pattern Scanner ? Les détections, leurs événements et les analyses source v1 seront supprimés.');
      if (!confirmed) {
        return;
      }
    }

    setLegacyPurgeLoading(true);
    setResetNotice('');
    setLegacyPurgeError('');
    setLegacyPurgeResult(null);
    try {
      const payload = await purgeLegacyPatternPerformanceV1();
      setLegacyPurgeResult(payload);
      await loadData(appliedFilters);
    } catch (loadError) {
      setLegacyPurgeError(loadError.message || 'Impossible de purger les données legacy v1.');
    } finally {
      setLegacyPurgeLoading(false);
    }
  }, [appliedFilters, loadData]);

  const actionsLoading = loading || cleanupLoading || purgeLoading || legacyPurgeLoading;

  const activeFilterCount = useMemo(
    () => Object.values(appliedFilters).filter((value) => String(value || '').trim() !== '').length,
    [appliedFilters],
  );

  const compareRows = useMemo(
    () => Object.entries(comparison || {})
      .map(([key, value]) => ({ engineVersion: value?.engineVersion || key, ...value }))
      .sort((left, right) => String(left.engineVersion).localeCompare(String(right.engineVersion))),
    [comparison],
  );

  const openDetail = useCallback(async (detectionId) => {
    setDetail(null);
    setDetailError('');
    setDetailLoading(true);
    try {
      const payload = await fetchPatternPerformanceDetail(detectionId);
      setDetail(payload);
    } catch (loadError) {
      setDetailError(loadError.message || 'Impossible de charger le détail de la détection.');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeDetail = () => {
    setDetail(null);
    setDetailError('');
  };

  const global = summary?.global;
  const lowSampleThreshold = summary?.lowSampleThreshold || calibration?.lowSampleThreshold || 30;
  const scoreCalibration = calibration?.score?.length ? calibration.score : calibrationFallbackFromBuckets(summary?.scoreBuckets);
  const mtfCalibration = calibration?.mtfScore?.length ? calibration.mtfScore : calibrationFallbackFromBuckets(summary?.mtfBuckets);
  const confidenceCalibration = calibration?.confidence?.length ? calibration.confidence : calibrationFallbackFromBuckets(summary?.confidenceBuckets);

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #efe8dc 0%, #edf3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between" alignItems={{ xs: 'stretch', lg: 'center' }}>
            <Box sx={{ maxWidth: 820 }}>
              <Chip
                icon={<AnalyticsRoundedIcon sx={{ color: '#f7f3ea !important' }} />}
                label="Admin Pattern Performance"
                sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }}
              />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Performance empirique du Pattern Scanner
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Cette page suit chaque détection depuis sa bougie de validation jusqu à sa résolution pour comparer objectivement les versions du moteur, mesurer la qualité historique du score pattern, du score MTF et de la confiance, sans altérer les statistiques brutes du tracking.
              </Typography>
            </Box>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
              <Chip label={`${global?.detections || 0} détection(s)`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }} />
              <Chip label={`${activeFilterCount} filtre(s) actif(s)`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }} />
              <Chip label={`Low sample < ${lowSampleThreshold}`} sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }} />
              <Chip
                label={summary?.cohortStart && summary?.cohortEnd ? `Cohorte ${formatDateTime(summary.cohortStart)} → ${formatDateTime(summary.cohortEnd)}` : 'Cohorte n/a'}
                sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700 }}
              />
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 5, background: '#fffaf2' }}>
          <Stack spacing={2.2}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.5 }}>
                  Filtres d analyse
                </Typography>
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Filtrez la cohorte par version, build, famille, type d actif, score, score MTF, alignement, volume et période de détection.
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap">
                <Button variant="contained" startIcon={<SearchRoundedIcon />} onClick={handleApplyFilters} sx={{ bgcolor: '#17323c', '&:hover': { bgcolor: '#0f2329' } }}>
                  Rechercher
                </Button>
                <Button variant="outlined" startIcon={<RestartAltRoundedIcon />} onClick={handleResetFilters} sx={{ borderColor: '#17323c', color: '#17323c' }}>
                  Réinitialiser
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteSweepRoundedIcon />}
                  onClick={handleCleanupDuplicates}
                  disabled={actionsLoading}
                  sx={{ borderColor: '#9c4f4f', color: '#9c4f4f' }}
                >
                  {cleanupLoading ? 'Nettoyage…' : 'Nettoyer doublons'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteSweepRoundedIcon />}
                  onClick={handlePurgeHistory}
                  disabled={actionsLoading}
                  sx={{ borderColor: '#7d2f2f', color: '#7d2f2f' }}
                >
                  {purgeLoading ? 'Purge…' : 'Purger historique'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DeleteSweepRoundedIcon />}
                  onClick={handlePurgeLegacyV1}
                  disabled={actionsLoading}
                  sx={{ borderColor: '#5f2120', color: '#5f2120' }}
                >
                  {legacyPurgeLoading ? 'Purge v1…' : 'Purger legacy v1'}
                </Button>
                <Button variant="text" startIcon={<RefreshRoundedIcon />} onClick={() => loadData(appliedFilters)} disabled={loading} sx={{ color: '#17323c' }}>
                  Actualiser
                </Button>
              </Stack>
            </Stack>

            {cleanupError ? (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {cleanupError}
              </Alert>
            ) : null}

            {purgeError ? (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {purgeError}
              </Alert>
            ) : null}

            {legacyPurgeError ? (
              <Alert severity="error" sx={{ borderRadius: 3 }}>
                {legacyPurgeError}
              </Alert>
            ) : null}

            {cleanupResult ? (
              <Alert severity={cleanupResult.deletedDetections > 0 ? 'success' : 'info'} sx={{ borderRadius: 3 }}>
                {cleanupResult.deletedDetections > 0
                  ? `${cleanupResult.deletedDetections} doublon(s) supprimé(s) sur ${cleanupResult.duplicateGroups} groupe(s), avec ${cleanupResult.deletedEvents} événement(s) lié(s) supprimé(s).`
                  : `Aucun doublon à supprimer sur la cohorte courante (${cleanupResult.scannedDetections} détection(s) analysée(s)).`}
              </Alert>
            ) : null}

            {purgeResult ? (
              <Alert severity={purgeResult.deletedDetections > 0 ? 'success' : 'info'} sx={{ borderRadius: 3 }}>
                {purgeResult.deletedDetections > 0
                  ? `${purgeResult.deletedDetections} détection(s) et ${purgeResult.deletedEvents} événement(s) supprimé(s) sur la période filtrée.`
                  : `Aucune détection à purger sur la période filtrée (${purgeResult.scannedDetections} détection(s) trouvée(s)).`}
              </Alert>
            ) : null}

            {legacyPurgeResult ? (
              <Alert severity={legacyPurgeResult.deletedDetections > 0 || legacyPurgeResult.deletedAnalyses > 0 ? 'success' : 'info'} sx={{ borderRadius: 3 }}>
                {legacyPurgeResult.deletedDetections > 0 || legacyPurgeResult.deletedAnalyses > 0
                  ? `${legacyPurgeResult.deletedDetections} détection(s) v1, ${legacyPurgeResult.deletedEvents} événement(s) et ${legacyPurgeResult.deletedAnalyses} analyse(s) source supprimé(s).`
                  : 'Aucune donnée legacy v1 à purger.'}
              </Alert>
            ) : null}

            {resetNotice ? (
              <Alert severity="info" sx={{ borderRadius: 3 }}>
                {resetNotice}
              </Alert>
            ) : null}

            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Par défaut, les statistiques excluent les stablecoins historisés. Le score et le score MTF servent uniquement aux filtres, aux buckets et aux calibrations. Les métriques globales restent brutes. Les scores MTF absents restent en bucket UNKNOWN et ne sont jamais forcés à 0. Le nettoyage des doublons agit sur la cohorte affichée. La purge historique agit sur les filtres actuellement saisis et exige Date début plus Date fin. Le bouton legacy v1 purge globalement les détections v1, leurs événements et les analyses source associées.
            </Alert>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(5, minmax(0, 1fr))' }, gap: 1.2 }}>
              <TextField label="Version(s)" size="small" value={filters.version} onChange={handleFilterChange('version')} placeholder="v1,v2" />
              <TextField label="Engine build" size="small" value={filters.engineBuild} onChange={handleFilterChange('engineBuild')} placeholder="v2-2026-08-14.1842" />
              <TextField label="Pattern" size="small" value={filters.pattern} onChange={handleFilterChange('pattern')} placeholder="DOUBLE_TOP" />
              <TextField label="Famille pattern" size="small" value={filters.patternFamily} onChange={handleFilterChange('patternFamily')} placeholder="REVERSAL" />
              <TextField label="Crypto" size="small" value={filters.crypto} onChange={handleFilterChange('crypto')} placeholder="BTC" />
              <TextField label="Exchange" size="small" value={filters.exchange} onChange={handleFilterChange('exchange')} placeholder="BINANCE" />
              <FormControl size="small">
                <InputLabel>Timeframe</InputLabel>
                <Select label="Timeframe" value={filters.timeframe} onChange={handleFilterChange('timeframe')}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="1H">1H</MenuItem>
                  <MenuItem value="4H">4H</MenuItem>
                  <MenuItem value="1D">1D</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Direction</InputLabel>
                <Select label="Direction" value={filters.direction} onChange={handleFilterChange('direction')}>
                  <MenuItem value="">Toutes</MenuItem>
                  <MenuItem value="BULLISH">LONG</MenuItem>
                  <MenuItem value="BEARISH">SHORT</MenuItem>
                  <MenuItem value="NEUTRAL">NEUTRAL</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Statut</InputLabel>
                <Select label="Statut" value={filters.status} onChange={handleFilterChange('status')}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                  <MenuItem value="BROKEN_OUT">BROKEN_OUT</MenuItem>
                  <MenuItem value="TARGET_HIT">TARGET_HIT</MenuItem>
                  <MenuItem value="INVALIDATED">INVALIDATED</MenuItem>
                  <MenuItem value="EXPIRED">EXPIRED</MenuItem>
                  <MenuItem value="AMBIGUOUS">AMBIGUOUS</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Bucket score</InputLabel>
                <Select label="Bucket score" value={filters.scoreBucket} onChange={handleFilterChange('scoreBucket')}>
                  <MenuItem value="">Tous</MenuItem>
                  {STANDARD_BUCKET_OPTIONS.map((bucket) => <MenuItem key={bucket} value={bucket}>{bucket}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Score min" size="small" type="number" value={filters.scoreMin} onChange={handleFilterChange('scoreMin')} inputProps={{ min: 0, max: 100 }} />
              <TextField label="Score max" size="small" type="number" value={filters.scoreMax} onChange={handleFilterChange('scoreMax')} inputProps={{ min: 0, max: 100 }} />
              <FormControl size="small">
                <InputLabel>Bucket score MTF</InputLabel>
                <Select label="Bucket score MTF" value={filters.mtfBucket} onChange={handleFilterChange('mtfBucket')}>
                  <MenuItem value="">Tous</MenuItem>
                  {MTF_BUCKET_OPTIONS.map((bucket) => <MenuItem key={bucket} value={bucket}>{bucket}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField label="Score MTF min" size="small" type="number" value={filters.mtfScoreMin} onChange={handleFilterChange('mtfScoreMin')} inputProps={{ min: 0, max: 100 }} />
              <TextField label="Score MTF max" size="small" type="number" value={filters.mtfScoreMax} onChange={handleFilterChange('mtfScoreMax')} inputProps={{ min: 0, max: 100 }} />
              <FormControl size="small">
                <InputLabel>Alignement MTF</InputLabel>
                <Select label="Alignement MTF" value={filters.mtfAlignment} onChange={handleFilterChange('mtfAlignment')}>
                  <MenuItem value="">Tous</MenuItem>
                  {MTF_ALIGNMENT_OPTIONS.map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Bucket confiance</InputLabel>
                <Select label="Bucket confiance" value={filters.confidenceBucket} onChange={handleFilterChange('confidenceBucket')}>
                  <MenuItem value="">Tous</MenuItem>
                  {STANDARD_BUCKET_OPTIONS.map((bucket) => <MenuItem key={bucket} value={bucket}>{bucket}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Type d actif</InputLabel>
                <Select label="Type d actif" value={filters.assetType} onChange={handleFilterChange('assetType')}>
                  {ASSET_TYPE_OPTIONS.map((option) => <MenuItem key={option.value || 'default'} value={option.value}>{option.label}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small">
                <InputLabel>Volume confirmé</InputLabel>
                <Select label="Volume confirmé" value={filters.volumeConfirmed} onChange={handleFilterChange('volumeConfirmed')}>
                  <MenuItem value="">Tous</MenuItem>
                  <MenuItem value="true">Oui</MenuItem>
                  <MenuItem value="false">Non</MenuItem>
                </Select>
              </FormControl>
              <TextField label="Date début" size="small" type="datetime-local" value={filters.dateFrom} onChange={handleFilterChange('dateFrom')} InputLabelProps={{ shrink: true }} />
              <TextField label="Date fin" size="small" type="datetime-local" value={filters.dateTo} onChange={handleFilterChange('dateTo')} InputLabelProps={{ shrink: true }} />
            </Box>
          </Stack>
        </Paper>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Paper sx={{ p: 6, borderRadius: 5, textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#19323c', mb: 1.5 }} />
            <Typography variant="body2" sx={{ color: '#4f646f' }}>
              Chargement des métriques de détection...
            </Typography>
          </Paper>
        ) : !summary ? (
          <Alert severity="info">Aucune statistique de performance n est disponible pour les filtres courants.</Alert>
        ) : (
          <>
            <Paper sx={{ borderRadius: 5, overflow: 'hidden', border: `1px solid ${alpha(PAGE_COLORS.ink, 0.08)}` }}>
              <Tabs
                value={activeTab}
                onChange={(_, nextValue) => setActiveTab(nextValue)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{
                  px: 1,
                  bgcolor: '#fff',
                  '& .MuiTabs-indicator': { backgroundColor: PAGE_COLORS.accent, height: 3 },
                }}
              >
                {TAB_OPTIONS.map((tab) => (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    label={tab.label}
                    sx={{
                      minHeight: 68,
                      color: PAGE_COLORS.muted,
                      fontWeight: 700,
                      '&.Mui-selected': { color: PAGE_COLORS.ink },
                    }}
                  />
                ))}
              </Tabs>
            </Paper>

            {activeTab === 'overview' ? (
              <Stack spacing={1.5}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.2 }}>
                  {[
                    { label: 'Détections', value: global?.detections || 0 },
                    { label: 'Breakouts', value: global?.breakoutCount || 0 },
                    { label: 'Targets finales', value: global?.targetFinalCount || 0, tone: 'success' },
                    { label: 'Invalidations', value: global?.invalidationCount || 0, tone: 'danger' },
                    { label: 'Expirés', value: global?.expiredCount || 0, tone: 'warning' },
                    { label: 'Ambigus', value: global?.ambiguousCount || 0, tone: 'warning' },
                    { label: 'Target rate', value: formatPercent(global?.targetFinalRate), tone: 'success' },
                    { label: 'Quality score', value: formatPercent(global?.qualityScore), tone: 'accent' },
                  ].map((item) => (
                    <MetricCard key={item.label} label={item.label} value={item.value} tone={item.tone} />
                  ))}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.05fr 0.95fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Lecture score / MTF"
                    description="Mesures descriptives globales pour lire la cohorte sans changer les statistiques de base."
                  >
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.2 }}>
                      <MetricCard label="Score pattern moyen" value={formatScore(global?.averagePatternScore, 1)} helper={`Median ${formatScore(global?.medianPatternScore, 1)}`} />
                      <MetricCard label="Score MTF moyen" value={formatScore(global?.averageMtfScore, 1)} helper={`Median ${formatScore(global?.medianMtfScore, 1)}`} tone="accent" />
                      <MetricCard label="Confiance moyenne" value={formatPercent(global?.averageConfidence)} helper={`Median ${formatPercent(global?.medianConfidence)}`} />
                    </Box>
                    <Divider />
                    <Stack spacing={1.2}>
                      <RateBar label="Breakout rate" value={global?.breakoutRate} color={PAGE_COLORS.inkSoft} />
                      <RateBar label="Target final rate" value={global?.targetFinalRate} color={PAGE_COLORS.success} />
                      <RateBar label="Invalidation rate" value={global?.invalidationRate} color={PAGE_COLORS.danger} />
                    </Stack>
                  </SectionCard>

                  <SectionCard
                    icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Statistiques temporelles"
                    description="Durées médianes et extrêmes utiles pour qualifier la vitesse de résolution."
                  >
                    {[
                      { label: 'Breakout median', value: formatDuration(global?.timeToBreakout?.medianSeconds) },
                      { label: 'Breakout moyenne', value: formatDuration(global?.timeToBreakout?.averageSeconds) },
                      { label: 'Target median', value: formatDuration(global?.timeToTargetFinal?.medianSeconds) },
                      { label: 'Target moyenne', value: formatDuration(global?.timeToTargetFinal?.averageSeconds) },
                      { label: 'Invalidation median', value: formatDuration(global?.timeToInvalidation?.medianSeconds) },
                      { label: 'P90 target', value: formatDuration(global?.timeToTargetFinal?.p90Seconds) },
                    ].map((item) => (
                      <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.75, borderBottom: `1px solid ${PAGE_COLORS.border}` }}>
                        <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>{item.label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{item.value}</Typography>
                      </Box>
                    ))}
                  </SectionCard>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Calibration de confiance"
                    description="Lecture rapide de la stabilité historique par bucket de confiance."
                  >
                    <BucketMetricsTable rows={summary.confidenceBuckets} lowSampleThreshold={lowSampleThreshold} firstLabel="Bucket confiance" />
                  </SectionCard>

                  <SectionCard
                    icon={<CompareArrowsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Comparaison des versions"
                    description="Benchmark ciblé des versions du moteur sur la cohorte filtrée."
                  >
                    {compareRows.length === 0 ? (
                      <Alert severity="info">Aucune version comparable pour la cohorte actuelle.</Alert>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 800 }}>Version</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Score moyen</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>MTF moyen</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Median target</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {compareRows.map((row) => (
                              <TableRow key={row.engineVersion} hover>
                                <TableCell>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.engineVersion}</Typography>
                                    <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                                  </Stack>
                                </TableCell>
                                <TableCell>{row.detections}</TableCell>
                                <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
                                <TableCell>{formatPercent(row.targetRate)}</TableCell>
                                <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
                                <TableCell>{formatScore(row.averagePatternScore, 1)}</TableCell>
                                <TableCell>{formatScore(row.averageMtfScore, 1)}</TableCell>
                                <TableCell>{formatDuration(row.medianTimeToTargetSeconds)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </SectionCard>
                </Box>

                <SectionCard
                  icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Dernières détections suivies"
                  description="Snapshot historisé enrichi avec score pattern, score MTF, alignement et confirmation volume."
                >
                  {summary.recentDetections?.length ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800 }}>Actif</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Pattern</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Version</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Score</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Score MTF</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Alignement</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Volume</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Statut</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Confiance</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Détection</TableCell>
                            <TableCell sx={{ fontWeight: 800 }} align="right">Action</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {summary.recentDetections.map((row) => (
                            <TableRow key={row.id} hover>
                              <TableCell>{row.symbol} / {row.exchange} / {row.timeframe}</TableCell>
                              <TableCell>{formatValue(row.assetType, 'UNKNOWN')}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.pattern}</Typography>
                                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{formatValue(row.patternFamily)}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{formatValue(row.engineVersion)}</Typography>
                                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>{formatValue(row.engineBuild)}</Typography>
                              </TableCell>
                              <TableCell>{formatScore(row.patternScore)}</TableCell>
                              <TableCell>{formatScore(row.mtfScore)}</TableCell>
                              <TableCell>
                                <Typography variant="body2">{formatValue(row.mtfAlignment, 'UNKNOWN')}</Typography>
                                <Typography variant="caption" sx={{ color: PAGE_COLORS.muted }}>Conviction {formatScore(row.convictionScore)}</Typography>
                              </TableCell>
                              <TableCell>{row.volumeConfirmation === true ? 'Confirmé' : row.volumeConfirmation === false ? 'Non' : 'n/a'}</TableCell>
                              <TableCell>{formatValue(row.status)}</TableCell>
                              <TableCell>{formatPercent(row.confidence)}</TableCell>
                              <TableCell>{formatDateTime(row.detectionCandleAt)}</TableCell>
                              <TableCell align="right">
                                <Button size="small" variant="outlined" onClick={() => openDetail(row.id)}>
                                  Timeline
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">Aucune détection historisée n est encore disponible.</Alert>
                  )}
                </SectionCard>
              </Stack>
            ) : null}

            {activeTab === 'patterns' ? (
              <Stack spacing={1.5}>
                <SectionCard
                  icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Performance par pattern"
                  description="Vue consolidée des familles les plus fiables, avec score moyen et version dominante."
                >
                  {summary.patterns?.length ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 800 }}>Pattern</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Quality</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Score moyen</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>MTF moyen</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Median target</TableCell>
                            <TableCell sx={{ fontWeight: 800 }}>Best version</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {summary.patterns.map((row) => (
                            <TableRow key={row.pattern} hover>
                              <TableCell>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.pattern}</Typography>
                                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                                </Stack>
                              </TableCell>
                              <TableCell>{row.detections}</TableCell>
                              <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
                              <TableCell>{formatPercent(row.targetFinalRate)}</TableCell>
                              <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
                              <TableCell>{formatPercent(row.qualityScore)}</TableCell>
                              <TableCell>{formatScore(row.averagePatternScore, 1)}</TableCell>
                              <TableCell>{formatScore(row.averageMtfScore, 1)}</TableCell>
                              <TableCell>{formatDuration(row.medianTimeToTargetSeconds)}</TableCell>
                              <TableCell>{formatValue(row.bestEngineVersion)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Alert severity="info">Aucun pattern ne correspond aux filtres actifs.</Alert>
                  )}
                </SectionCard>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Pattern × bucket score"
                    description="Permet d identifier les patterns qui gardent un target rate stable selon le score brut."
                  >
                    <GroupedMetricsTable rows={summary.patternScoreBuckets} groupLabel="Pattern" lowSampleThreshold={lowSampleThreshold} />
                  </SectionCard>
                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Pattern × bucket MTF"
                    description="Lecture croisée du pattern et de son contexte multi-timeframe historisé."
                  >
                    <GroupedMetricsTable rows={summary.patternMtfBuckets} groupLabel="Pattern" lowSampleThreshold={lowSampleThreshold} />
                  </SectionCard>
                </Box>
              </Stack>
            ) : null}

            {activeTab === 'versions' ? (
              <Stack spacing={1.5}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<CompareArrowsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Cohorte par version"
                    description="Lecture complète des versions présentes dans la cohorte, même hors benchmark ciblé."
                  >
                    {summary.versions?.length ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 800 }}>Version</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Breakout</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Score moyen</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>MTF médian</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Median target</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {summary.versions.map((row) => (
                              <TableRow key={row.engineVersion} hover>
                                <TableCell>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.engineVersion}</Typography>
                                    <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                                  </Stack>
                                </TableCell>
                                <TableCell>{row.detections}</TableCell>
                                <TableCell>{formatPercent(row.breakoutRate)}</TableCell>
                                <TableCell>{formatPercent(row.targetFinalRate)}</TableCell>
                                <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
                                <TableCell>{formatScore(row.averagePatternScore, 1)}</TableCell>
                                <TableCell>{formatScore(row.medianMtfScore, 1)}</TableCell>
                                <TableCell>{formatDuration(row.medianTimeToTargetSeconds)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">Aucune version n est disponible pour la cohorte courante.</Alert>
                    )}
                  </SectionCard>

                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Benchmark ciblé"
                    description="Compare les versions demandées à iso-filtres pour juger le drift ou le gain."
                  >
                    {compareRows.length === 0 ? (
                      <Alert severity="info">Aucune comparaison ciblée n est disponible.</Alert>
                    ) : (
                      <Stack spacing={1.2}>
                        {compareRows.map((row) => (
                          <Paper key={row.engineVersion} variant="outlined" sx={{ p: 1.8, borderRadius: 3 }}>
                            <Stack spacing={1.1}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                  <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{row.engineVersion}</Typography>
                                  <Chip size="small" label={`${row.detections} détection(s)`} sx={{ fontWeight: 700 }} />
                                  <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                                </Stack>
                                <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>
                                  Quality {formatPercent(row.qualityScore)}
                                </Typography>
                              </Stack>
                              <RateBar label="Breakout" value={row.breakoutRate} color={PAGE_COLORS.inkSoft} />
                              <RateBar label="Target final" value={row.targetRate} color={PAGE_COLORS.success} />
                              <RateBar label="Invalidation" value={row.invalidationRate} color={PAGE_COLORS.danger} />
                              <RateBar label="Expiration" value={row.expirationRate} color={PAGE_COLORS.warning} />
                              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                                <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>Score moyen: <strong>{formatScore(row.averagePatternScore, 1)}</strong></Typography>
                                <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>MTF moyen: <strong>{formatScore(row.averageMtfScore, 1)}</strong></Typography>
                                <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>Median target: <strong>{formatDuration(row.medianTimeToTargetSeconds)}</strong></Typography>
                                <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>Cohorte: <strong>{formatDateTime(row.cohortStart)} → {formatDateTime(row.cohortEnd)}</strong></Typography>
                              </Box>
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </SectionCard>
                </Box>

                <SectionCard
                  icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Version × bucket score"
                  description="Répartition historique du target rate par version et tranche de score."
                >
                  <GroupedMetricsTable rows={summary.versionScoreBuckets} groupLabel="Version" lowSampleThreshold={lowSampleThreshold} />
                </SectionCard>
              </Stack>
            ) : null}

            {activeTab === 'score' ? (
              <Stack spacing={1.5}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '1fr 1fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Buckets score pattern"
                    description="Calibration brute du score pattern sans pondération artificielle des statistiques."
                  >
                    <BucketMetricsTable rows={summary.scoreBuckets} lowSampleThreshold={lowSampleThreshold} firstLabel="Bucket score" />
                  </SectionCard>
                  <SectionCard
                    icon={<InsightsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Score × confirmation volume"
                    description="Permet de vérifier si la confirmation volume améliore ou non la qualité du score."
                  >
                    {summary.scoreVolumeBuckets?.length ? (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell sx={{ fontWeight: 800 }}>Bucket</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Volume</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Détections</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Target</TableCell>
                              <TableCell sx={{ fontWeight: 800 }}>Invalidation</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {summary.scoreVolumeBuckets.map((row) => (
                              <TableRow key={`${row.scoreBucket}-${row.volumeLabel}`} hover>
                                <TableCell>
                                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.scoreBucket}</Typography>
                                    <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                                  </Stack>
                                </TableCell>
                                <TableCell>{formatValue(row.volumeLabel)}</TableCell>
                                <TableCell>{row.detections}</TableCell>
                                <TableCell>{formatPercent(row.targetFinalRate)}</TableCell>
                                <TableCell>{formatPercent(row.invalidationRate)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Alert severity="info">Aucune coupe volume n est encore disponible.</Alert>
                    )}
                  </SectionCard>
                </Box>

                <SectionCard
                  icon={<TimelineRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                  title="Calibration score pattern"
                  description="Lecture bucket par bucket pour voir si un score plus élevé correspond réellement à de meilleurs outcomes."
                >
                  <Stack spacing={1.2}>
                    {scoreCalibration.map((row) => (
                      <Paper key={row.bucket} variant="outlined" sx={{ p: 1.6, borderRadius: 3, bgcolor: PAGE_COLORS.sand }}>
                        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'center' }}>
                          <Box sx={{ minWidth: 140 }}>
                            <Typography variant="h6" sx={{ fontWeight: 800, color: PAGE_COLORS.ink }}>{row.bucket}</Typography>
                            <Typography variant="body2" sx={{ color: PAGE_COLORS.muted }}>{row.sampleSize} détection(s)</Typography>
                            <LowSampleChip lowSample={row.lowSample} threshold={lowSampleThreshold} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <RateBar label="Breakout" value={row.breakoutRate} color={PAGE_COLORS.inkSoft} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <RateBar label="Target final" value={row.targetRate} color={PAGE_COLORS.success} />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <RateBar label="Invalidation" value={row.invalidationRate} color={PAGE_COLORS.danger} />
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                </SectionCard>
              </Stack>
            ) : null}

            {activeTab === 'mtf' ? (
              <Stack spacing={1.5}>
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  Le score MTF reflète la reconstruction historique du contexte 1H / 4H / 1D. Quand ce contexte n est pas disponible, le bucket reste UNKNOWN et n est jamais converti en 0.
                </Alert>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: '0.9fr 1.1fr' }, gap: 1.5 }}>
                  <SectionCard
                    icon={<AnalyticsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Buckets score MTF"
                    description="Répartition des outcomes par tranche de score multi-timeframe historisé."
                  >
                    <BucketMetricsTable rows={summary.mtfBuckets} lowSampleThreshold={lowSampleThreshold} firstLabel="Bucket MTF" />
                  </SectionCard>
                  <SectionCard
                    icon={<CompareArrowsRoundedIcon sx={{ color: PAGE_COLORS.ink }} />}
                    title="Matrice score × MTF"
                    description="Croise le score pattern brut et le score MTF pour repérer les zones de cohérence ou de conflit."
                  >
                    <ScoreMtfMatrix cells={summary.scoreMtfMatrix} lowSampleThreshold={lowSampleThreshold} />
                  </SectionCard>
                </Box>
              </Stack>
            ) : null}

            {activeTab === 'calibration' ? (
              <Stack spacing={1.5}>
                {calibrationError ? <Alert severity="warning">{calibrationError}</Alert> : null}
                <CalibrationPanel
                  title="Calibration du score pattern"
                  description="Mesure si le score pattern historisé est réellement prédictif des outcomes."
                  rows={scoreCalibration}
                  lowSampleThreshold={lowSampleThreshold}
                />
                <CalibrationPanel
                  title="Calibration du score MTF"
                  description="Mesure l apport du contexte multi-timeframe indépendamment du score brut."
                  rows={mtfCalibration}
                  lowSampleThreshold={lowSampleThreshold}
                />
                <CalibrationPanel
                  title="Calibration de la confiance"
                  description="Met en regard la confiance, le target rate et l invalidation sur l historique réel."
                  rows={confidenceCalibration}
                  lowSampleThreshold={lowSampleThreshold}
                />
              </Stack>
            ) : null}
          </>
        )}

        <Dialog open={Boolean(detail) || detailLoading || Boolean(detailError)} onClose={closeDetail} maxWidth="lg" fullWidth>
          <DialogTitle>Timeline de détection pattern</DialogTitle>
          <DialogContent dividers>
            {detailLoading ? (
              <Box sx={{ py: 6, textAlign: 'center' }}>
                <CircularProgress sx={{ color: '#19323c', mb: 1.5 }} />
                <Typography variant="body2" sx={{ color: '#4f646f' }}>
                  Chargement du détail de détection...
                </Typography>
              </Box>
            ) : detailError ? (
              <Alert severity="error">{detailError}</Alert>
            ) : !detail ? (
              <Alert severity="info">Aucune détection sélectionnée.</Alert>
            ) : (
              <Stack spacing={2.5}>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} justifyContent="space-between">
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c' }}>
                        {detail.detection.symbol} / {detail.detection.exchange} / {detail.detection.timeframe}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#4f646f', mt: 0.5 }}>
                        Pattern {detail.detection.pattern} · Type {formatValue(detail.detection.assetType, 'UNKNOWN')} · Version {detail.detection.engineVersion} · Confiance {formatPercent(detail.detection.patternConfidence)} · Score {formatScore(detail.detection.patternScore)} · Score MTF {formatScore(detail.detection.multiTimeframeScore)}
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <Chip label={`Statut ${detail.evaluation.status}`} sx={{ fontWeight: 700 }} />
                      <Chip label={`Détection ${formatDateTime(detail.detection.detectionCandleAt)}`} sx={{ fontWeight: 700 }} />
                    </Stack>
                  </Stack>
                </Paper>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1.2 }}>
                  <MetricCard label="Score pattern" value={formatScore(detail.detection.patternScore)} helper={`Conviction ${formatScore(detail.detection.convictionScore)}`} />
                  <MetricCard label="Score MTF" value={formatScore(detail.detection.multiTimeframeScore)} helper={`Alignement ${formatValue(detail.detection.mtfAlignmentState, 'UNKNOWN')}`} tone="accent" />
                  <MetricCard label="Type actif" value={formatValue(detail.detection.assetType, 'UNKNOWN')} helper={`Version ${formatValue(detail.detection.engineVersion)}`} />
                  <MetricCard label="Confiance cible" value={formatPercent(detail.detection.targetConfidence)} helper={`Volume ${detail.detection.volumeConfirmation === true ? 'confirmé' : detail.detection.volumeConfirmation === false ? 'non confirmé' : 'n/a'}`} />
                  <MetricCard label="Volume aligné" value={formatScore(detail.detection.volumeAlignmentScore)} helper={`Ratio ${formatDecimal(detail.detection.breakoutVolumeRatio, 2)}`} />
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(3, minmax(0, 1fr))' }, gap: 1.2 }}>
                  {[
                    { label: 'Breakout', value: detail.detection.breakoutPrice },
                    { label: 'Target 1', value: detail.detection.target1Price },
                    { label: 'Target 2', value: detail.detection.target2Price },
                    { label: 'Target final', value: detail.detection.targetFinalPrice },
                    { label: 'Invalidation', value: detail.detection.invalidationPrice },
                    { label: 'Stop', value: detail.detection.stopPrice },
                  ].map((item) => (
                    <Paper key={item.label} variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
                      <Typography variant="caption" sx={{ color: '#4f646f' }}>{item.label}</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 800, color: '#17323c' }}>{item.value ?? 'n/a'}</Typography>
                    </Paper>
                  ))}
                </Box>

                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c', mb: 1.5 }}>
                    Timeline des événements
                  </Typography>
                  {detail.events?.length ? (
                    <Stack spacing={1.2}>
                      {detail.events.map((event) => (
                        <Box key={event.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1, borderBottom: '1px solid #efe8dc' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: '#17323c' }}>{event.eventType}</Typography>
                            <Typography variant="caption" sx={{ color: '#4f646f' }}>{formatDateTime(event.eventCandleAt)}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="body2" sx={{ color: '#17323c' }}>{event.eventPrice ?? 'n/a'}</Typography>
                            <Typography variant="caption" sx={{ color: '#4f646f' }}>{formatDuration(event.elapsedSeconds)} · {event.elapsedCandles ?? 0} bougie(s)</Typography>
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Alert severity="info">Aucun événement historisé pour cette détection.</Alert>
                  )}
                </Paper>

                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c', mb: 1.5 }}>
                    Durées et évaluation
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }, gap: 1.2 }}>
                    {[
                      { label: 'Time to breakout', value: formatDuration(detail.duration.timeToBreakoutSeconds) },
                      { label: 'Time to target final', value: formatDuration(detail.duration.timeToTargetFinalSeconds) },
                      { label: 'Time to invalidation', value: formatDuration(detail.duration.timeToInvalidationSeconds) },
                      { label: 'Horizon', value: formatDateTime(detail.evaluation.evaluationUntil) },
                    ].map((item) => (
                      <Box key={item.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, py: 0.75, borderBottom: '1px solid #efe8dc' }}>
                        <Typography variant="body2" sx={{ color: '#4f646f' }}>{item.label}</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 800, color: '#17323c' }}>{item.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              </Stack>
            )}
          </DialogContent>
        </Dialog>
      </Stack>
    </Box>
  );
};

export default PatternPerformanceAdminPage;